import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';

// Modern shadcn components
import { AppSidebar } from '@/components/app-sidebar';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

// Search history utilities
import { searchHistoryApi } from '../../utils/searchHistoryApi';
import { SearchHistoryItem } from '../../utils/searchHistory';

// Icons
import {
  FileTextIcon, CalendarIcon, FlaskConicalIcon, TrendingUpIcon,
  ClockIcon, EyeIcon, SearchIcon, CheckCircleIcon, RefreshCwIcon, TrashIcon
} from 'lucide-react';

// SpyGlass Brand Icon - consistent with main app
const SpyglassBrandIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="4" width="12" height="12" rx="2" stroke="#4F46E5" strokeWidth="2" fill="none"/> {/* Indigo-600 (Top-Left Outlined Square) */}
    {/* Sky-500 (Top-Right Diamond - Rotated Square) */}
    <rect x="18" y="4" width="12" height="12" rx="2" fill="#0EA5E9" transform="rotate(45 24 10)"/> 
    {/* Blue-500 (Bottom-Left Triangle - Pointing Up) */}
    <path d="M4 30 L16 30 L10 18 Z" fill="#3B82F6"/> 
    <circle cx="24" cy="24" r="6" fill="#A5B4FC"/>                        {/* Indigo-300 (Bottom-Right Filled Circle) */}
  </svg>
);

export default function ReportsPage() {
  const { token: authToken } = useAuth();
  const navigate = useNavigate();
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);

  const loadHistoryData = async () => {
    setIsRefreshing(true);
    try {
      const history = await searchHistoryApi.loadSearchHistory();
      setSearchHistory(history);
    } catch (error) {
      console.error('Failed to load search history:', error);
      setSearchHistory([]);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    // Load search history from Supabase on component mount
    const loadInitialHistory = async () => {
      try {
        const history = await searchHistoryApi.loadSearchHistory();
        setSearchHistory(history);
      } catch (error) {
        console.error('Failed to load search history:', error);
        setSearchHistory([]);
      }
    };

    loadInitialHistory();
    
    // Listen for real-time updates to search history
    const handleSearchHistoryUpdate = async () => {
      try {
        const updatedHistory = await searchHistoryApi.loadSearchHistory();
        setSearchHistory(updatedHistory);
      } catch (error) {
        console.error('Failed to refresh search history:', error);
      }
    };

    // Smart header scroll behavior
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        // Scrolling down & past threshold - hide header
        setIsHeaderVisible(false);
      } else if (currentScrollY < lastScrollY.current) {
        // Scrolling up - show header
        setIsHeaderVisible(true);
      }
      
      lastScrollY.current = currentScrollY;
    };
    
    window.addEventListener('searchHistoryUpdated', handleSearchHistoryUpdate);
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('searchHistoryUpdated', handleSearchHistoryUpdate);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewReport = (taskId: string) => {
    navigate(`/dashboard/${taskId}`);
  };

  const handleDeleteReport = async (taskId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await searchHistoryApi.deleteSearchResult(taskId);
      // Refresh the list after deletion
      await loadHistoryData();
    } catch (error) {
      console.error('Failed to delete report:', error);
      alert('Failed to delete report. Please try again.');
    }
  };

  const handleClearAll = async () => {
    // Show permission denied message - users should not be able to clear all reports
    alert('You don\'t have permission to clear all analysis reports. Contact your administrator if you need to manage the data.');
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className={`flex h-16 shrink-0 items-center gap-2 border-b px-4 sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-transform duration-300 ${isHeaderVisible ? 'translate-y-0' : '-translate-y-full'}`}>
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/" className="flex items-center gap-2">
                  <SpyglassBrandIcon />
                  <span className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    SpyGlass AI
                  </span>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbPage>Reports & History</BreadcrumbPage>
            </BreadcrumbList>
          </Breadcrumb>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/')}>
              <SearchIcon className="h-4 w-4 mr-2" />
              New Analysis
            </Button>
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
          <div className="min-h-[100vh] flex-1 rounded-xl bg-muted/50 p-4 md:p-6">
            
            {/* Header Section */}
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold tracking-tight">Analysis Reports</h1>
                  <p className="text-muted-foreground mt-1">
                    View and manage your product analysis history
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="secondary" className="h-8">
                    <FileTextIcon className="h-4 w-4 mr-1" />
                    {searchHistory.length} Reports
                  </Badge>
                  <Button variant="outline" size="sm" onClick={loadHistoryData} disabled={isRefreshing}>
                    <RefreshCwIcon className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                    {isRefreshing ? 'Refreshing...' : 'Refresh'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleClearAll}>
                    <TrashIcon className="h-4 w-4 mr-2" />
                    Clear All
                  </Button>
                </div>
              </div>
            </div>

            {/* Search History Grid */}
            {searchHistory.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {searchHistory.map((report) => (
                  <Card key={report.id} className="relative overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group" onClick={() => handleViewReport(report.id)}>
                    <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500 to-purple-500"></div>
                    <CardHeader className="pl-6 pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg leading-tight mb-1">{report.productName}</CardTitle>
                          <CardDescription className="text-sm">{report.objective}</CardDescription>
                        </div>
                        {report.status === 'completed' ? (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            <CheckCircleIcon className="h-3 w-3 mr-1" />
                            Complete
                          </Badge>
                        ) : report.status === 'processing' ? (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            <ClockIcon className="h-3 w-3 mr-1" />
                            Processing
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                            <ClockIcon className="h-3 w-3 mr-1" />
                            Failed
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="pl-6 pt-0">
                      <div className="space-y-3">
                        {report.status === 'completed' ? (
                          <>
                            {/* Key Metrics for Completed Reports */}
                            <div className="grid grid-cols-3 gap-2 text-center">
                              <div className="p-2 bg-blue-50 rounded">
                                <div className="text-sm font-bold text-blue-600">{report.costReduction || 0}%</div>
                                <div className="text-xs text-blue-800">Cost Reduction</div>
                              </div>
                              <div className="p-2 bg-green-50 rounded">
                                <div className="text-sm font-bold text-green-600">
                                  {report.marketValue ? `$${(report.marketValue / 1000000000).toFixed(1)}B` : 'N/A'}
                                </div>
                                <div className="text-xs text-green-800">Market Value</div>
                              </div>
                              <div className="p-2 bg-purple-50 rounded">
                                <div className="text-sm font-bold text-purple-600">{report.consumerInterest || 'N/A'}/10</div>
                                <div className="text-xs text-purple-800">Interest</div>
                              </div>
                            </div>
                          </>
                        ) : report.status === 'processing' ? (
                          <>
                            {/* Processing Status */}
                            <div className="flex items-center justify-center p-4 bg-blue-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <ClockIcon className="h-5 w-5 text-blue-600 animate-pulse" />
                                <div className="text-center">
                                  <div className="text-sm font-medium text-blue-800">Analysis in Progress</div>
                                  <div className="text-xs text-blue-600">This may take 3-5 minutes</div>
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            {/* Failed Status */}
                            <div className="flex items-center justify-center p-4 bg-red-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <ClockIcon className="h-5 w-5 text-red-600" />
                                <div className="text-center">
                                  <div className="text-sm font-medium text-red-800">Analysis Failed</div>
                                  <div className="text-xs text-red-600">Please try again</div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}

                        {/* Timestamp and Action */}
                        <div className="flex items-center justify-between pt-2 border-t">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <CalendarIcon className="h-4 w-4" />
                            {formatTimestamp(report.timestamp)}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewReport(report.id);
                            }}
                          >
                            <EyeIcon className="h-4 w-4 mr-1" />
                            {report.status === 'processing' ? 'View Progress' : 'View'}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="text-center py-12">
                <CardContent>
                  <FlaskConicalIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Analysis Reports Yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Start your first product analysis to see reports here
                  </p>
                  <Button onClick={() => navigate('/')}>
                    <SearchIcon className="h-4 w-4 mr-2" />
                    Start New Analysis
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Recent Activity Summary */}
            {searchHistory.length > 0 && (
              <div className="mt-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUpIcon className="h-5 w-5" />
                      Analysis Summary
                    </CardTitle>
                    <CardDescription>Overview of your recent analysis activity</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-4">
                      <div className="text-center p-4 bg-muted/50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">{searchHistory.length}</div>
                        <div className="text-sm text-muted-foreground">Total Analyses</div>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">
                          {searchHistory.filter(r => r.status === 'completed').length}
                        </div>
                        <div className="text-sm text-green-800">Completed</div>
                      </div>
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">
                          {searchHistory.filter(r => r.status === 'processing').length}
                        </div>
                        <div className="text-sm text-blue-800">In Progress</div>
                      </div>
                      <div className="text-center p-4 bg-yellow-50 rounded-lg">
                        <div className="text-2xl font-bold text-yellow-600">
                          {searchHistory.filter(r => r.status === 'completed').length > 0 ? 
                            Math.round(searchHistory.filter(r => r.status === 'completed').reduce((acc, report) => acc + (report.costReduction || 0), 0) / searchHistory.filter(r => r.status === 'completed').length) + '%' 
                            : 'N/A'
                          }
                        </div>
                        <div className="text-sm text-yellow-800">Avg. Cost Reduction</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
} 